/**
 * Language Switcher Component
 *
 * Dropdown component for switching the application language
 */

import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { updatePreferencesAsync, selectLocale } from '../../store/slices/preferencesSlice';
import type { AppDispatch } from '../../store';

/**
 * Available language options
 * Note: Only languages supported by i18n configuration (en, es) are listed here.
 * Region-specific codes like es-ES, en-US are not supported by the i18n backend.
 */
const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
] as const;

export interface LanguageSwitcherProps {
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

/**
 * Language Switcher Component
 * 
 * @example
 * ```tsx
 * <LanguageSwitcher variant="outlined" size="small" />
 * ```
 */
export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'outlined',
  size = 'medium',
  fullWidth = false,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const dispatch = useDispatch<AppDispatch>();
  const currentLocale = useSelector(selectLocale);

  const handleLanguageChange = async (event: SelectChangeEvent<string>) => {
    const newLocale = event.target.value;
    
    try {
      const result = await dispatch(updatePreferencesAsync({ locale: newLocale }));
      if (result && 'success' in result && !result.success) {
        console.error('Failed to update language preference');
      }
    } catch (error) {
      console.error('Failed to update language preference:', error);
    }
  };

  return (
    <FormControl variant={variant} size={size} fullWidth={fullWidth}>
      <InputLabel id="language-select-label">
        {t('settings.language')}
      </InputLabel>
      <Select
        labelId="language-select-label"
        id="language-select"
        value={currentLocale}
        label={t('settings.language')}
        onChange={handleLanguageChange}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem key={lang.code} value={lang.code}>
            {lang.nativeName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSwitcher;
